import React, { useEffect, useState, useContext } from 'react';
import api, { resolveScopedRestaurantId } from '../../utils/api';
import toast from '../../utils/notifications';
import { dashboardRefreshEmitter } from '../../utils/dashboardRefresh';
import { AuthContext } from '../../context/AuthContext';

export default function OrdersManagement() {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all');

  const [showTableCard, setShowTableCard] = useState(false);
  const [pendingPrintOrder, setPendingPrintOrder] = useState(null);
  const [selectedTableNumber, setSelectedTableNumber] = useState('');

  const normalizedRole = (role) => String(role || '').toLowerCase().trim();
  const isStaff = normalizedRole(user?.role) === 'staff';
  const isAdmin = normalizedRole(user?.role) === 'admin';

  const LAKEHOUSE_TABLE_MAP_KEY = 'lakehouse_reprint_tables';

  const isLakehouseOrder = (order) => Number.parseInt(order?.restaurant_id, 10) === 2;

  const getStoredTableMap = () => {
    try {
      const raw = localStorage.getItem(LAKEHOUSE_TABLE_MAP_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  };

  const saveStoredTableMap = (tableMap) => {
    localStorage.setItem(LAKEHOUSE_TABLE_MAP_KEY, JSON.stringify(tableMap));
  };

  const getExistingLakehouseTableNumber = (order) => {
    if (!isLakehouseOrder(order)) {
      return '';
    }

    const tableMap = getStoredTableMap();
    const orderKey = String(order.order_id);
    const existing = Number.parseInt(tableMap[orderKey], 10);

    return Number.isInteger(existing) && existing >= 1 && existing <= 25 ? String(existing) : '';
  };

  const persistLakehouseTableNumber = (order, tableNumber) => {
    if (!isLakehouseOrder(order)) {
      return;
    }

    const parsed = Number.parseInt(tableNumber, 10);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 25) {
      return;
    }

    const tableMap = getStoredTableMap();
    tableMap[String(order.order_id)] = parsed;
    saveStoredTableMap(tableMap);
  };

  const getOrderCustomerName = (order) => {
    const fullName = String(order?.user?.full_name || '').trim();
    if (fullName) return fullName;

    const firstName = String(order?.user?.first_name || '').trim();
    const lastName = String(order?.user?.last_name || '').trim();
    const combined = `${firstName} ${lastName}`.trim();
    if (combined) return combined;

    const email = String(order?.user?.email || '').trim();
    return email || 'Guest';
  };

  const getOrderSubtotal = (order) => {
    const storedSubtotal = Number.parseFloat(order?.subtotal);
    if (Number.isFinite(storedSubtotal) && storedSubtotal > 0) {
      return storedSubtotal;
    }

    const itemSubtotal = Array.isArray(order?.items)
      ? order.items.reduce((total, item) => {
          const price = Number.parseFloat(item?.price || 0);
          const qty = Number.parseInt(item?.quantity || 0, 10);
          return total + (Number.isFinite(price) ? price : 0) * (Number.isInteger(qty) ? qty : 0);
        }, 0)
      : 0;

    if (itemSubtotal > 0) {
      return itemSubtotal;
    }

    return Number.parseFloat(order?.total_amount || 0) || 0;
  };

  const getDisplayOrderNumber = (value) => {
    const raw = String(value || '');

    // Normalize common formats like CG-2026/03/19-00001 or CG-2026-03-19-00001 to CG-20260319-00001
    const normalized = raw.replaceAll('/', '-');
    const parts = normalized.split('-');

    if (
      parts.length >= 5 &&
      /^\d{4}$/.test(parts[1]) &&
      /^\d{2}$/.test(parts[2]) &&
      /^\d{2}$/.test(parts[3])
    ) {
      const prefix = parts[0];
      const compactDate = `${parts[1]}${parts[2]}${parts[3]}`;
      const sequence = parts.slice(4).join('-');
      return `${prefix}-${compactDate}-${sequence}`;
    }

    return normalized;
  };

  const executePrintReceipt = (order, tableNumber = '') => {
    const printWindow = window.open('', '_blank', 'width=302,height=500');
    const now = new Date(order.created_at);
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Receipt - Order #${getDisplayOrderNumber(order.receipt_number || order.order_id)}</title>
        <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap" rel="stylesheet">
        <style>
          @page { size: 58mm auto; margin: 1mm; }
          body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; width: 56mm; margin: 0; padding: 1mm; line-height: 1.4; }
          .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 2mm; margin-bottom: 2mm; }
          .logo { width: 22mm; height: 22mm; margin: 0 auto 1mm; border-radius: 50%; object-fit: cover; }
          .header h1 { font-family: 'Bebas Neue', cursive; font-size: 16pt; margin: 0 0 0.5mm 0; font-weight: bold; letter-spacing: 1.5px; }
          .header p { font-size: 9pt; margin: 0.5mm 0; font-weight: bold; }
          .info { font-size: 9pt; margin-bottom: 2mm; line-height: 1.5; }
          .items { border-top: 2px dashed #000; border-bottom: 2px dashed #000; padding: 2mm 0; margin: 2mm 0; }
          .item { display: flex; justify-content: space-between; margin: 1.5mm 0; font-size: 9pt; font-weight: 500; }
          .item-name { flex: 1; font-weight: normal; }
          .item-qty { width: 10mm; text-align: center; font-weight: normal; }
          .item-price { width: 18mm; text-align: right; font-weight: bold; }
          .totals { margin-top: 2mm; font-size: 10pt; }
          .total-row { display: flex; justify-content: space-between; margin: 1mm 0; }
          .total-row.grand { font-weight: bold; font-size: 13pt; border-top: 1px solid #000; padding-top: 1mm; margin-top: 2mm; letter-spacing: 0.5px; }
          .footer { text-align: center; margin-top: 3mm; padding-top: 2mm; border-top: 1px dashed #000; font-size: 9pt; line-height: 1.6; }
          .footer .bold { font-family: 'Bebas Neue', cursive; font-size: 13pt; letter-spacing: 2px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="/images/casa-gatchalian-logo.jpg" alt="Casa Gatchalian" class="logo" />
          <h1>CASA GATCHALIAN</h1>
          <p>Restobar</p>
        </div>

        <div class="info">
          <div>Date: ${dateStr} ${timeStr}</div>
          <div>Order #: ${getDisplayOrderNumber(order.receipt_number || order.order_id)}</div>
          ${tableNumber ? `<div>Table: ${tableNumber}</div>` : ''}
          <div>Ordered by: ${getOrderCustomerName(order)}</div>
        </div>

        <div class="items">
          ${(order.items || []).map(item => `
            <div class="item">
              <div class="item-name">${item.menu_item?.name || item.name || 'Unknown'}</div>
              <div class="item-qty">x${item.quantity}</div>
              <div class="item-price">₱${parseFloat(item.price * item.quantity).toFixed(2)}</div>
            </div>
          `).join('')}
          ${order.takeout_canister_count > 0 ? `
            <div class="item" style="border-top: 1px solid #ccc; padding-top: 1mm; margin-top: 1.5mm;">
              <div class="item-name">Takeout Canister</div>
              <div class="item-qty">x${order.takeout_canister_count}</div>
              <div class="item-price">₱${parseFloat(order.takeout_canister_count * 25).toFixed(2)}</div>
            </div>
          ` : ''}
        </div>

        <div class="totals">
          ${order.takeout_canister_count > 0 ? `
            <div class="total-row">
              <span>Subtotal (Food):</span>
              <span>₱${parseFloat((order.subtotal || order.total_amount) - (order.takeout_canister_count * 25)).toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Canister (${order.takeout_canister_count}x):</span>
              <span>₱${parseFloat(order.takeout_canister_count * 25).toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row">
            <span>Subtotal:</span>
            <span>₱${parseFloat(order.subtotal || order.total_amount).toFixed(2)}</span>
          </div>
          ${(order.discount_amount && order.discount_amount > 0) ? `
            <div class="total-row" style="color: #16a34a; font-weight: bold;">
              <span>Discount (${order.discount_percentage || 20}%):</span>
              <span>-₱${parseFloat(order.discount_amount).toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row grand">
            <span>TOTAL:</span>
            <span>₱${parseFloat(order.total_amount).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Payment:</span>
            <span>${order.payment_method === 'cash' ? 'Cash' : 'Online Payment'}</span>
          </div>
          <div class="total-row">
            <span>Status:</span>
            <span>${order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
          </div>
        </div>

        <div class="footer">
          <p class="bold">THANK YOU!</p>
          <p>Please come again</p>
          <p style="margin-top: 2mm; font-size: 7pt;">Not an official receipt for inventory use only.</p>
          <p style="margin-top: 2mm; border-bottom: 1px dashed #000; padding-bottom: 2mm;">&nbsp;</p>
          <p style="margin-top: 5mm;">&nbsp;</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();

    printWindow.onload = function () {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };

    printWindow.onafterprint = function () {
      printWindow.close();
    };

    setTimeout(() => {
      try {
        if (printWindow && !printWindow.closed) {
          printWindow.close();
        }
      } catch (e) {
        // Ignore.
      }
    }, 2000);
  };

  const handleConfirmTableAndPrint = () => {
    if (!pendingPrintOrder) {
      return;
    }

    const parsed = Number.parseInt(selectedTableNumber, 10);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 25) {
      toast.error('Please select a valid table number from 1 to 25.');
      return;
    }

    const tableValue = String(parsed);
    persistLakehouseTableNumber(pendingPrintOrder, tableValue);
    setShowTableCard(false);
    executePrintReceipt(pendingPrintOrder, tableValue);
    setPendingPrintOrder(null);
    setSelectedTableNumber('');
  };

  const handleCancelTableCard = () => {
    setShowTableCard(false);
    setPendingPrintOrder(null);
    setSelectedTableNumber('');
  };

  const printReceipt = (order) => {
    if (isLakehouseOrder(order)) {
      setPendingPrintOrder(order);
      setSelectedTableNumber(getExistingLakehouseTableNumber(order));
      setShowTableCard(true);
      return;
    }

    executePrintReceipt(order);
  };

  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const response = await api.get('/restaurants');
        setRestaurants(response.data.data || []);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
      }
    };
    loadRestaurants();
  }, []);

  useEffect(() => {
    if (user) {
      if (isStaff && user.restaurant_id) {
        setSelectedRestaurant(user.restaurant_id.toString());
        localStorage.setItem('selected_restaurant_id', user.restaurant_id.toString());
      } else if (isAdmin) {
        const savedRestaurantId = localStorage.getItem('selected_restaurant_id');
        const initial = savedRestaurantId || resolveScopedRestaurantId(user)?.toString() || '';
        setSelectedRestaurant(initial);
        if (initial) {
          localStorage.setItem('selected_restaurant_id', initial);
        }
      }
    }
  }, [user, isStaff, isAdmin]);

  useEffect(() => {
    if (selectedRestaurant && selectedRestaurant.trim()) {
      localStorage.setItem('selected_restaurant_id', selectedRestaurant.trim());
      return;
    }

    if (isAdmin) {
      localStorage.removeItem('selected_restaurant_id');
    }
  }, [selectedRestaurant, isAdmin]);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [selectedRestaurant, user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {};

      if (selectedRestaurant && selectedRestaurant.trim()) {
        params.restaurant_id = parseInt(selectedRestaurant, 10);
      } else if (isStaff && user?.restaurant_id) {
        params.restaurant_id = user.restaurant_id;
      }

      const response = await api.get('/orders', { params });
      if (response.data.success) {
        const ordersData = response.data.data?.data || response.data.data || [];
        const sortedOrders = Array.isArray(ordersData)
          ? ordersData.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
          : [];
        setOrders(sortedOrders);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      toast.error('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await api.put(`/orders/${orderId}`, { status: newStatus });
      if (response.data.success) {
        toast.success(`Order status updated to ${newStatus}`);
        fetchOrders();
        dashboardRefreshEmitter.emit();
      }
    } catch (err) {
      console.error('Error updating order:', err);
      toast.error('Failed to update order status');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/orders/${orderId}`);
      if (response.data.success) {
        toast.success('Order deleted successfully');
        fetchOrders();
        if (selectedOrder && selectedOrder.order_id === orderId) {
          setSelectedOrder(null);
        }
        dashboardRefreshEmitter.emit();
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      toast.error('Failed to delete order');
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      preparing: 'bg-purple-100 text-purple-800 border-purple-300',
      ready: 'bg-green-100 text-green-800 border-green-300',
      served: 'bg-teal-100 text-teal-800 border-teal-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Orders Management</h2>
          <p className="mt-1 text-xs sm:text-sm text-gray-600">View and manage all restaurant orders</p>
        </div>
      </div>

      {isAdmin && restaurants.length > 0 && (
        <div className="bg-white shadow rounded-lg p-3 sm:p-4">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Select Restaurant</label>
          <select
            value={selectedRestaurant}
            onChange={(e) => setSelectedRestaurant(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
          >
            <option value="">Choose a restaurant</option>
            {restaurants.map((restaurant) => (
              <option key={restaurant.restaurant_id} value={restaurant.restaurant_id.toString()}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-3 sm:p-4">
        <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Status Filter</h3>
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'completed', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                filter === status ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredOrders.length === 0 ? (
          <div className="col-span-full text-center py-8 sm:py-12 bg-white shadow rounded-lg">
            <p className="text-sm sm:text-base text-gray-500">No orders found</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.order_id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-4 gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">Order</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-800 break-words leading-tight mt-0.5">#{getDisplayOrderNumber(order.receipt_number || order.order_id)}</p>
                  </div>
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap shrink-0 ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="text-xs sm:text-sm text-gray-600">
                    <p className="font-medium text-gray-700">Customer:</p>
                    <p className="truncate">{getOrderCustomerName(order)}</p>
                  </div>

                  <div className="text-xs sm:text-sm text-gray-600">
                    <p className="font-medium text-gray-700">Order Date:</p>
                    <p className="truncate">
                      {new Date(order.created_at).toLocaleString('en-PH', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>

                  <div className="text-xs sm:text-sm text-gray-600">
                    <p className="font-medium text-gray-700">Payment Method:</p>
                    <p>{order.payment_method || 'Cash'}</p>
                  </div>

                  {(order.items && order.items.length > 0) || order.takeout_canister_count > 0 ? (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-2">Order Items:</p>
                      <div className="space-y-1">
                        {order.items && order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-gray-600">
                            <span>{item.quantity}x {item.menu_item?.name || item.name || 'Item'}</span>
                            <span>₱{parseFloat((item.price || 0) * item.quantity).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        {order.takeout_canister_count > 0 && (
                          <div className="flex justify-between text-xs text-gray-600 pt-1 border-t border-gray-100">
                            <span>{order.takeout_canister_count}x Takeout Canister{order.takeout_canister_count !== 1 ? 's' : ''}</span>
                            <span>₱{parseFloat(order.takeout_canister_count * 25).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className="text-xs sm:text-sm text-gray-600 pt-3 border-t border-gray-200">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Subtotal:</span>
                        <span>₱{parseFloat(getOrderSubtotal(order)).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                      </div>
                      {order.discount_amount && order.discount_amount > 0 && (
                        <div className="flex justify-between text-xs text-green-600 font-semibold">
                          <span>Discount ({order.discount_percentage || 20}%):</span>
                          <span>-₱{parseFloat(order.discount_amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-1 border-t border-gray-300">
                        <p className="font-medium text-gray-700">Total Amount:</p>
                        <p className="text-base sm:text-lg font-semibold text-black">
                          ₱{parseFloat(order.total_amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <select
                    value={order.status}
                    onChange={(e) => updateOrderStatus(order.order_id, e.target.value)}
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="px-2 sm:px-3 py-2 bg-black text-white rounded-md text-xs sm:text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={() => printReceipt(order)}
                      className="px-2 sm:px-3 py-2 bg-green-600 text-white rounded-md text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Print
                    </button>
                    {!isStaff && (
                      <button
                        onClick={() => deleteOrder(order.order_id)}
                        className="px-2 sm:px-3 py-2 bg-red-600 text-white rounded-md text-xs sm:text-sm font-medium hover:bg-red-700 transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">Order #{getDisplayOrderNumber(selectedOrder.receipt_number || selectedOrder.order_id)}</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Customer</p>
                  <p className="font-semibold">{getOrderCustomerName(selectedOrder)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Order Date</p>
                  <p className="font-semibold">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <p className="font-semibold">{selectedOrder.payment_method || 'Cash'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                  </span>
                </div>
              </div>

              {(selectedOrder.items && selectedOrder.items.length > 0) || selectedOrder.takeout_canister_count > 0 ? (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Order Items</h3>
                  <ul className="space-y-2">
                    {selectedOrder.items && selectedOrder.items.map((item, idx) => (
                      <li key={idx} className="flex justify-between">
                        <span>{item.quantity}x {item.menu_item?.name || item.name || 'Item'}</span>
                        <span className="font-semibold">₱{parseFloat(item.price * item.quantity).toLocaleString()}</span>
                      </li>
                    ))}
                    {selectedOrder.takeout_canister_count > 0 && (
                      <li className="flex justify-between pt-2 border-t">
                        <span>{selectedOrder.takeout_canister_count}x Takeout Canister{selectedOrder.takeout_canister_count !== 1 ? 's' : ''}</span>
                        <span className="font-semibold">₱{parseFloat(selectedOrder.takeout_canister_count * 25).toLocaleString()}</span>
                      </li>
                    )}
                  </ul>
                  <div className="border-t mt-4 pt-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal:</span>
                        <span>₱{parseFloat(getOrderSubtotal(selectedOrder)).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                      </div>
                      {selectedOrder.discount_amount && selectedOrder.discount_amount > 0 && (
                        <div className="flex justify-between text-sm text-green-600 font-semibold">
                          <span>Discount ({selectedOrder.discount_percentage || 20}%):</span>
                          <span>-₱{parseFloat(selectedOrder.discount_amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                        <span>Total</span>
                        <span className="text-blue-600">₱{parseFloat(selectedOrder.total_amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-6 flex justify-between gap-2">
              {!isStaff && (
                <button
                  onClick={() => deleteOrder(selectedOrder.order_id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Delete Order
                </button>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => printReceipt(selectedOrder)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  Print Receipt
                </button>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTableCard && pendingPrintOrder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-indigo-100 w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
              <h3 className="text-lg font-semibold">Lakehouse Table Selection</h3>
              <p className="text-xs text-indigo-100 mt-1">
                Order #{getDisplayOrderNumber(pendingPrintOrder.receipt_number || pendingPrintOrder.order_id)}
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select table number (1-25)</label>
                <select
                  value={selectedTableNumber}
                  onChange={(e) => setSelectedTableNumber(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Choose table</option>
                  {Array.from({ length: 25 }, (_, index) => {
                    const table = index + 1;
                    return (
                      <option key={table} value={String(table)}>
                        Table {table}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={handleCancelTableCard}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmTableAndPrint}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  Save and Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
